'use client';

import { isJSON, isTruthyString } from '@dealerportal/utilities';
import 'react-multi-carousel/lib/styles.css';
import classNames from 'classnames';
import { lazy } from 'react';

const Carousel = lazy(() => import('react-multi-carousel'));

export type GalleryProps = {
  removeArrowOnDeviceType?: string | string[];
  arrows?: string;
  arrowposition?: 'inside' | 'outside';
  autoPlay?: string | boolean;
  autoplay?: string;
  autoplayinterval?: string;
  autoplayspeed?: string;
  centermode?: string;
  containerclass?: string;
  customtransition?: string;
  dotlistclass?: string;
  infinite?: string;
  numberperrow?: string;
  items?: string;
  itemclass?: string;
  partialvisible?: string;
  partialvisibilitygutter?: string;
  renderdotsoutside?: string;
  responsive?: string;
  bullets?: string;
  showdots?: string;
  numbertoscroll?: string;
  slidestoslide?: string;
  sliderclass?: string;
  transitionduration?: string;
  presentation?: string;
  ssr?: boolean;
  children?: React.ReactElement | React.ReactElement[];
};

export function Gallery(props: GalleryProps) {
  const {
    arrows,
    arrowposition,
    autoPlay,
    autoplay,
    autoplayinterval,
    autoplayspeed,
    centermode,
    containerclass,
    customtransition,
    dotlistclass,
    infinite,
    numberperrow,
    items,
    itemclass,
    partialvisible,
    partialvisibilitygutter,
    removeArrowOnDeviceType,
    renderdotsoutside,
    responsive,
    bullets,
    showdots,
    numbertoscroll,
    slidestoslide,
    sliderclass,
    transitionduration,
    presentation,
    children,
    ssr,
  } = props;

  const arrowClass = classNames({
    'ui-border ui-border-black ui-opacity-80 ui-absolute ui-bg-black ui-py-2':
      arrowposition == null || arrowposition === 'inside',
    'ui-opacity-100 ui-absolute ui-bg-black ui-py-2 ui--mx-6':
      arrowposition === 'outside',
    '': arrowposition !== 'inside' && arrowposition !== 'outside',
  });

  const CustomArrow = ({
    onClick,
    direction,
  }: {
    onClick?: () => void;
    direction: 'left' | 'right';
  }) => (
    <button
      className={classNames(
        arrowClass,
        '!ui-bg-white',
        '!ui-rounded-full',
        '!ui-w-[2.5rem]',
        '!ui-h-[2.5rem]',
        'ui-flex',
        'ui-items-center',
        'ui-justify-center',
        {
          'ui-left-[1.5rem]':
            direction === 'left' && arrowposition === 'outside',
          'ui-left-[20px]': direction === 'left' && arrowposition !== 'outside',
          'ui-right-[1.5rem]':
            direction === 'right' && arrowposition === 'outside',
          'ui-right-[20px]':
            direction === 'right' && arrowposition !== 'outside',
        }
      )}
      onClick={onClick}
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 448 512"
        className="ui-w-6 ui-h-6 ui-text-black !ui-fill-black"
      >
        <path
          d={
            direction === 'left'
              ? 'M7.4 238.6c-4.7 4.5-7.4 10.8-7.4 17.4s2.7 12.8 7.4 17.4l176 168c9.6 9.2 24.8 8.8 33.9-.8s8.8-24.8-.8-33.9L83.9 280H424c13.3 0 24-10.7 24-24s-10.7-24-24-24H83.9l133.6-126.6c9.6-9.2 9.9-24.3.8-33.9s-24.3-9.9-33.9-.8l-176 168z'
              : 'M440.6 273.4c4.7-4.5 7.4-10.8 7.4-17.4s-2.7-12.8-7.4-17.4l-176-168c-9.6-9.2-24.8-8.8-33.9 .8s-8.8 24.8 .8 33.9L364.1 232 24 232c-13.3 0-24 10.7-24 24s10.7 24 24 24l340.1 0L231.4 406.6c-9.6 9.2-9.9 24.3-.8 33.9s24.3 9.9 33.9 .8l176-168z'
          }
        />
      </svg>
    </button>
  );

  const config: React.ComponentPropsWithoutRef<typeof Carousel> = {
    arrows: !!isTruthyString(arrows),
    customLeftArrow: <CustomArrow direction="left" />,
    customRightArrow: <CustomArrow direction="right" />,
    autoPlay: !!(
      isTruthyString(autoplay) ||
      autoPlay === true ||
      isTruthyString(autoplayinterval)
    ),
    autoPlaySpeed: 3000,
    centerMode: !!isTruthyString(centermode),
    containerClass: containerclass || '',
    customTransition: customtransition || 'transform 1000ms ease-in-out',
    dotListClass: dotlistclass || '',
    infinite: !!(isTruthyString(infinite) || infinite === undefined),
    itemClass: `${
      Number(items) > 1 || Number(numberperrow) > 1 ? 'ui-px-4' : ''
    } ${itemclass || ''}`,
    partialVisible: !!isTruthyString(partialvisible),
    renderDotsOutside: !!isTruthyString(renderdotsoutside),
    removeArrowOnDeviceType: Array.isArray(removeArrowOnDeviceType)
      ? removeArrowOnDeviceType
      : removeArrowOnDeviceType?.split(',') || ['mobile', 'tablet'],
    showDots: !!(isTruthyString(showdots) || isTruthyString(bullets)),
    slidesToSlide: 1,
    ssr: ssr ?? false,
    sliderClass: `${presentation === 'carousel' ? 'ui-items-center' : ''} ${
      sliderclass || ''
    }`,
    transitionDuration: Number(transitionduration)
      ? Number(transitionduration)
      : 1000,
    responsive: {
      md: {
        breakpoint: { max: 767, min: 0 },
        items: 1,
        partialVisibilityGutter: Number(partialvisibilitygutter) || 0,
      },
      lg: {
        breakpoint: { max: 1023, min: 768 },
        items: 1,
        partialVisibilityGutter: Number(partialvisibilitygutter) || 0,
      },
      xl: {
        breakpoint: { max: 1279, min: 1024 },
        items: Number(numberperrow) || Number(items) || 1,
        partialVisibilityGutter: Number(partialvisibilitygutter) || 0,
        slidesToSlide: Number(slidestoslide) || 1,
      },
      '2xl': {
        breakpoint: { max: 99999, min: 1280 },
        items: Number(numberperrow) || Number(items) || 1,
        partialVisibilityGutter: Number(partialvisibilitygutter) || 0,
        slidesToSlide: Number(slidestoslide) || 1,
      },
    },
    children: undefined,
  };

  if (Number(autoplayinterval)) {
    config.autoPlaySpeed = Number(autoplayinterval) * 1000;
  }

  if (Number(autoplayspeed)) {
    config.autoPlaySpeed = Number(autoplayspeed);
  }

  if (Number(numbertoscroll)) {
    config.slidesToSlide = Number(numbertoscroll);
  }

  if (Number(slidestoslide)) {
    config.slidesToSlide = Number(slidestoslide);
  }

  if (isJSON(responsive)) {
    config.responsive = {
      ...config.responsive,
      ...JSON.parse(responsive || ''),
    };
  }

  return arrowposition === 'outside' ? (
    <div className="ui-relative ui-overflow-hidden ui-px-[4rem]">
      <Carousel
        className="!ui-position-unset ui-m-auto ui-mw-[1200px]"
        {...config}
      >
        {children}
      </Carousel>
    </div>
  ) : (
    <Carousel {...config}>{children}</Carousel>
  );
}

export default Gallery;
